import React, { useMemo, useState } from 'react';
import TableCommonHook from '@op/components/TableCommonHook';
import { DBO_V } from '@op/utils/format';
import { TabPanel, Tabs, StatusTip, Tag } from 'tea-component';
import CardCommon from '@op/components/CardCommon';
import { cardIcon, tableIcon } from '@op/constants/chart';
import { ClassSpecial } from '@op/constants/common';
import { LanguageType, PageInfoType } from '@op/types/common';
import { DBOM_CONTENT } from '@opm/utils';
import useIntls from '@opm/hooks/useIntls';
import VideoCard from './../../../../Videos/components/videoCard';
import VideoInfoCell from '@opm/components/VideoInfoCell';
import SelectDropdown from '@opm/components/SelectDropdown';
import SvgIcon from '@home/components/SvgIcon';
import { languageSelectOptions } from '@op/components/FilterBoxCommon/constant';
import TranslateSelect from '@op/components/SelectComponents/TranslateSelect';
import { NO_DATE } from '@op/constants';
import './index.scss';

type VideoCardTableType = {
  sortBy: any;
  setSortBy: (value: any) => void;
  language: LanguageType;
  data?: Array<any>;
  total: number;
  loading: boolean;
  pageInfo: PageInfoType;
  changePageInfo: (value: PageInfoType) => void;
  cardRecords: Array<any>;
  tableRecords: Array<any>;
};

interface ISortByOptions {
  labelId: string;
  key: string;
}

const sortByOptions: ISortByOptions[] = [
  { labelId: 'Views', key: 'tweets_view' },
  { labelId: 'Likes', key: 'tweets_like' },
  { labelId: 'Engagement', key: 'engagement' },
  { labelId: 'Video Duration', key: 'video_duration' },
  { labelId: 'Date', key: 'video_release_time' },
];

export default function HashtagVideoTableList({
  sortBy,
  setSortBy,
  keyword,
  setKeyword,
  download,
  operation,
  setOperation,
  changePageInfo,
  selectedKol,
  setSelectedKol,
  ...data
}: Omit<VideoCardTableType, 'language'> & {
  operation: 'card' | 'table';
  setOperation: (value: 'card' | 'table') => void;
  download: () => void;
  keyword?: string;
  setKeyword: (value: string) => void;
  selectedKol: {
    anchor_uid: string;
    anchor_name: string;
  }[];
  setSelectedKol: (kols: {
    anchor_uid: string;
    anchor_name: string;
  }[]
  ) => void;
}) {
  const { intl, formatOptionsMessage } = useIntls();
  const [language, setLanguage] = useState<LanguageType>('origin');

  return (
    <CardCommon
      className="dbom-video-table-cardCommon"
      alwaysShowOperations
      defaultOperation={operation}
      operations={[
        // {
        //   element: (
        //     <SearchBox
        //       onSearch={(value = '') => {
        //         setKeyword(value);
        //       }}
        //       onClear={() => {
        //         setKeyword('');
        //       }}
        //       placeholder={intl({ id: 'Search video' })}
        //     ></SearchBox>
        //   ),
        // },
        cardIcon,
        tableIcon,
      ]}
      hasDownload
      title={
        <div className='dbom-video-table-filter'>
          <SelectDropdown
            value={sortBy?.by}
            onChange={(value: any) => {
              setSortBy({ order: 'desc', by: value });
            }}
            options={sortByOptions}
            appearance="link"
            className="details-dropdown-filter"
            customButton={
              <div className="select-button">
                <span className="dbo-select-flex">{intl({
                  id: 'Videos Sort by {value}'
                }, {
                  value: intl(sortByOptions?.find((val) => val.key === sortBy?.by)?.labelId || NO_DATE.EMPTY)
                })}</span>
              </div>
            }
            overlayClassName={"dbom-video-table-filter overlay-class"}
          />
          <TranslateSelect
            isSingle={true}
            lang={language}
            langOptions={formatOptionsMessage(languageSelectOptions)}
            getChange={(e: any) => setLanguage(e)}
            originalText={'Original Language'}
          />
          {
            selectedKol?.length > 0 && (
              <div className='dbom-video-kol-selected'>
                {intl('Current selected')}:
                <>
                  {
                    selectedKol?.map((kol, index) => (
                      <Tag
                        key={index}
                        className="dbom-video-kol-tag"
                      >
                        <RenderImg coverImage={kol?.anchor_image} mode='image' />
                        <span className='dbom-video-kol-name'>{kol?.anchor_name}</span>
                        <SvgIcon type='clonse' className='dbom-video-kol-close' onClick={() => setSelectedKol([])} />
                      </Tag>
                    ))
                  }
                </>
              </div>
            )
          }
        </div>
      }
      handleOperationsOnClick={(item: any) => {
        setOperation(item.symbol);
      }}
      handleDownloadOnClick={download}
      contentCard
    >
      <Tabs activeId={operation} className={ClassSpecial.TAB_NONE}>
        <TabPanel id={'card'}>
          <VideoList
            language={language}
            changePageInfo={changePageInfo}
            {...data}
          ></VideoList>
        </TabPanel>
        <TabPanel id={'table'}>
          <VideoTable
            sortBy={sortBy}
            setSortBy={setSortBy}
            changePageInfo={changePageInfo}
            language={language}
            {...data}
          ></VideoTable>
        </TabPanel>
      </Tabs>
    </CardCommon>
  );
}

function VideoList({
  language,
  loading,
  cardRecords,
}: Omit<VideoCardTableType, 'setSortBy' | 'sortBy'>) {

  return (
    <CardCommon
      contentCard
      loading={loading && cardRecords.length === 0}
      loadingHeight={350}
      empty={cardRecords.length === 0}
      emptyHeight={350}
    >
      <>
        <div className="dbom-video-list">
          {cardRecords.map((item: any) => {
            return <VideoCard key={item.url} item={item} language={language} />;
          })}
        </div>
        {loading && cardRecords.length !== 0 && (
          <div className="loading-common dbom-loading-common">
            <StatusTip status="loading" />
          </div>
        )}
      </>
    </CardCommon>
  );
}

function VideoTable({
  sortBy,
  setSortBy,
  language,
  tableRecords = [],
  loading,
  total,
  pageInfo,
  changePageInfo,
}: VideoCardTableType) {
  const { intl, isEn } = useIntls();

  const columns = useMemo(() => {
    const list = [
      {
        key: 'video_release_time',
        header: intl('Video(Sort by Date)'),
        width: 600,
        render: (data: any) => {
          return (
            <VideoInfoCell
              data={data}
              titleMode="bubble"
              fieldMapping={{
                title: (d: any) => DBOM_CONTENT.videoTitle(language, d),
              }}
            />
          );
        },
      },
      {
        key: 'tweets_view',
        header: intl({ id: 'Views' }),
        align: 'right',
        render: (cvm: any) => {
          return <div>{DBO_V.reviews(cvm.views)}</div>;
        },
      },
      {
        key: 'engagement',
        header: intl({ id: 'Engagement' }),
        align: 'right',
        render: (cvm: any) => {
          return <div>{DBO_V.reviews(cvm.engagement)}</div>;
        },
      },
      {
        key: 'tweets_like',
        header: intl({ id: 'Likes' }),
        align: 'right',
        render: (cvm: any) => {
          return <div>{DBO_V.reviews(cvm.likes)}</div>;
        },
      },
    ];

    return list;
  }, [intl, language]);

  return (
    <TableCommonHook
      sortInfo={[sortBy!, setSortBy]}
      records={tableRecords}
      total={total}
      loading={loading}
      pageInfo={pageInfo}
      setPageInfo={changePageInfo}
      sortColumns={[
        'video_release_time',
        'tweets_view',
        'engagement',
        'tweets_like',
      ]}
      recordKey={(item) =>
        item.video_title +
        item.video_release_time +
        item.anchor_name +
        item.video_cover
      }
      columns={columns}
      minWidth={'auto'}
      tableSticky
      pageSizeOptions={[10, 20, 30, 40, 50, 100, 200]}
    ></TableCommonHook>
  );
}
